"use client";

import { useAuth } from "@clerk/nextjs";
import { ReactNode } from "react";

/**
 * Auth gate components that default to showing signed-out content
 * before Clerk hydrates. This ensures marketing content is present
 * in the initial HTML (for Googlebot and first paint), then swaps
 * to the correct state once Clerk loads on the client.
 */

export function SignedOut({
  children,
  waitForClerk = false,
}: {
  children: ReactNode;
  /**
   * Render nothing until Clerk resolves, instead of showing children
   * optimistically. Use it wherever the signed-out content takes up layout a
   * signed-in viewer would then lose — a bottom bar, a banner — since the
   * default's flash-then-vanish is a shift on every signed-in page load.
   * Leave it off for marketing copy, where being in the initial HTML for
   * Googlebot and first paint is the whole point.
   */
  waitForClerk?: boolean;
}) {
  const { isLoaded, isSignedIn } = useAuth();

  // Before Clerk loads: show children (marketing content in initial HTML),
  // unless the caller asked to wait.
  // After Clerk loads: show only if user is signed out
  if (isLoaded ? !isSignedIn : !waitForClerk) {
    return <>{children}</>;
  }

  return null;
}

export function SignedIn({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  // Before Clerk loads: hide (marketing content takes priority)
  // After Clerk loads: show only if user is signed in
  if (isLoaded && isSignedIn) {
    return <>{children}</>;
  }

  return null;
}
